import { cn } from "@babackapp/customer-ui/lib/utils";
import type { SuggestionItem } from "../../hooks/use-resolution-suggestions";
import { ArrowUpRight, Sparkles } from "lucide-react";
import { motion } from "motion/react";

type Props = {
  title: string;
  items: SuggestionItem[];
  onSelect: (item: SuggestionItem) => void;
};

function SuggestionTile({
  item,
  onSelect,
}: {
  item: SuggestionItem;
  onSelect: (item: SuggestionItem) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onSelect(item)}
      className={cn(
        "group flex flex-row items-center gap-2 rounded-lg border bg-background",
        "p-1 text-left transition-colors",
        "hover:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
      )}
    >
      <div className="size-6 overflow-hidden rounded-md bg-muted shrink-0">
        {item.imageUrl ? (
          <motion.img
            layoutId={`suggestion-image-${item.id}`}
            src={item.imageUrl}
            alt={item.subtitle}
            className="size-full object-cover"
          />
        ) : (
          <motion.div
            layoutId={`suggestion-image-${item.id}`}
            className="size-full bg-muted"
          />
        )}
      </div>
      <div className=" min-w-0 text-sm flex flex-col">
        <p className="text-muted-foreground">{item.label}</p>
        <p className="truncate font-medium leading-tight text-foreground">
          {item.subtitle}
        </p>
      </div>
      <ArrowUpRight className="stroke-1 transition-all group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-active:-translate-x-0.5 group-active:translate-y-0.5 size-3" />
    </button>
  );
}

export function SuggestionGrid({ title, items, onSelect }: Props) {
  if (items.length === 0) return null;

  return (
    <>
      <style>{`
        @keyframes ai-border-shift {
          0%   { background-position: 0% 50%; }
          50%  { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
      `}</style>
      <div
        className="rounded-xl p-[2.5px]"
        style={{
          background:
            "linear-gradient(135deg, #c4b5fd, #fdba74, #86efac, #c4b5fd, #fdba74)",
          backgroundSize: "300% 300%",
          animation: "ai-border-shift 5s ease infinite",
        }}
      >
        <div className="flex flex-col gap-3 rounded-[10px] bg-white p-3">
          <div className="flex flex-col gap-0.5">
            <p className="text-sm font-medium text-foreground">
              <Sparkles className="size-4 text-violet-300 inline mr-1 -translate-y-0.5" />
              {title}
            </p>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {items.map((item) => (
              <SuggestionTile key={item.id} item={item} onSelect={onSelect} />
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
